// Venues — the record of what a venue is (F-ORG-01; spec/04 §3, spec/13 §13, spec/14 §3.3, spec/08 §4.3).
//
//   GET   /v1/organizations/{o}/venues                   the organisation's venues   venues:read
//   POST  /v1/organizations/{o}/venues                   create one                  venues:create
//   GET   /v1/organizations/{o}/venues/{venueId}         one, with an ETag           venues:read
//   PATCH /v1/organizations/{o}/venues/{venueId}         edit, If-Match required     venues:update
//   POST  /v1/organizations/{o}/venues/{venueId}/archive close it for good           venues:archive
//   PATCH /v1/hq/organizations/{o}/venues/{v}/listing    HQ's discovery flags        hq:organizations (platform)
//   GET   /v1/hq/venues                                  every organisation's venues hq:organizations (platform)
//
// Scope is explicit (spec/08 §1.3): the organisation and the venue come from the path. A venue of
// another organisation is `404`, never `403` (§2.6). Each route names the permission that gates it
// (`x-clavian-permission`), which is what the server's guard enforces.
//
// **Concurrency.** A venue is edited by more than one person — an owner in the console, a manager
// on a tablet, HQ setting a listing flag — so it carries an `ETag` and an update must send the one
// it read as `If-Match` (§2.5). A stale one is `412 stale-write`; a missing one is `400`, because a
// write that did not say which version it meant to change is exactly the write that silently
// overwrites someone else's.
//
// `currency` is fixed at creation and must be the organisation's (rule 5). `timezone` may only change
// while the venue is in `setup`: once it trades, it decides which business day every order and
// session was filed under, and moving it would re-bucket history under the owner's feet. A trading
// venue's `businessDayCutoff` change takes effect from the next business day (ADR-0029): the day in
// progress and every past day keep theirs (`businessDayCutoffHistory`).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { FileIdSchema, OrganizationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { AddressSchema, CoordinateSchema } from "../primitives/address.js";
import { HolidaysSchema, OpeningHoursSchema } from "../primitives/opening-hours.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { PhoneNumberSchema } from "../primitives/phone-number.js";
import { CurrencyCodeSchema, IsoDateTimeSchema, LocalDateSchema, LocalTimeSchema, TimezoneSchema, } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema, VenueScopeParamsSchema } from "../primitives/scope.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { HQ_ORGANIZATIONS, VENUES_ARCHIVE, VENUES_CREATE, VENUES_READ, VENUES_UPDATE, } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
// ---------------------------------------------------------------------------------------------
// The shapes
// ---------------------------------------------------------------------------------------------
/** spec/04 §3. `closed` is reached only by archiving (`venues:archive`). */
export const VENUE_STATUSES = ['setup', 'live', 'paused', 'closed'];
export const VenueStatusSchema = z.enum(VENUE_STATUSES).openapi('VenueStatus', {
    description: '`setup` before trading, `live`, `paused` (temporarily shut), `closed` (archived for good).',
});
/** The statuses an edit may set. `closed` is not one of them: that is the archive endpoint. */
export const EditableVenueStatusSchema = z.enum(['setup', 'live', 'paused']).openapi('EditableVenueStatus');
/** spec/13 §13: "parking, AC, food, washrooms, streaming room". */
export const VENUE_AMENITIES = ['parking', 'air_conditioning', 'food', 'washrooms', 'streaming_room'];
export const VenueAmenitySchema = z.enum(VENUE_AMENITIES).openapi('VenueAmenity');
export const VenueAmenitiesSchema = z
    .array(VenueAmenitySchema)
    .max(VENUE_AMENITIES.length)
    .refine((amenities) => new Set(amenities).size === amenities.length, { message: 'List each amenity once.' })
    .openapi('VenueAmenities');
/** A venue slug: lower-kebab, the `{venueSlug}` in the console URL and the player app's venue page. */
export const VenueSlugSchema = z
    .string()
    .min(3)
    .max(60)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lower-case letters, digits and single hyphens, e.g. playground-central')
    .openapi('VenueSlug', { example: 'playground-central' });
export const VenueContactSchema = z
    .object({
    phone: PhoneNumberSchema.optional(),
    email: z.email().max(254).optional(),
    website: z
        .url({ protocol: /^https$/ })
        .max(2048)
        .optional()
        .openapi({ description: 'https only.' }),
})
    .strict()
    .openapi('VenueContact', { description: 'How players reach the venue. Shown on the public venue page.' });
/** The largest gallery a venue page shows. */
export const MAX_VENUE_GALLERY_PHOTOS = 20;
/** One size of an image the worker made from an upload (spec/03 §7.4). */
export const ImageVariantSchema = z
    .object({
    width: z.int().positive(),
    url: z.url(),
})
    .openapi('ImageVariant');
export const VenuePhotoSchema = z
    .object({
    fileId: FileIdSchema,
    /** The original, served from the CDN. */
    url: z.url(),
    /** Resized copies, smallest first, for `srcset`. The worker makes them seconds after upload. */
    variants: z.array(ImageVariantSchema),
})
    .openapi('VenuePhoto');
/**
 * An uploaded image other than a venue photo — a venue's logo, a menu product's picture: its file, the
 * original on the CDN, and the worker's resized copies (spec/03 §7.4). The same shape as a photo.
 */
export const UploadedImageSchema = z
    .object({
    fileId: FileIdSchema,
    /** The original, served from the CDN. */
    url: z.url(),
    /** Resized copies, smallest first, for `srcset`. The worker makes them seconds after upload. */
    variants: z.array(ImageVariantSchema),
})
    .openapi('UploadedImage', { description: 'An uploaded image: its file, the original’s URL and its resized copies.' });
export const VenuePhotosSchema = z
    .object({
    cover: VenuePhotoSchema.nullable(),
    gallery: z.array(VenuePhotoSchema).max(MAX_VENUE_GALLERY_PHOTOS),
})
    .openapi('VenuePhotos', { description: 'A cover and a gallery (spec/13 §13).' });
/** What an edit sends: the uploaded files by id. Each must be a `venue_photo` of this venue that has completed. */
export const VenuePhotoRefsSchema = z
    .object({
    cover: FileIdSchema.nullable(),
    gallery: z.array(FileIdSchema).max(MAX_VENUE_GALLERY_PHOTOS),
})
    .strict()
    .refine((refs) => new Set(refs.gallery).size === refs.gallery.length, { message: 'A photo appears in the gallery once.' })
    .openapi('VenuePhotoRefs');
/** An earlier business-day cutoff and the last business day it decided (ADR-0029). */
export const VenueCutoffPeriodSchema = z
    .object({ until: LocalDateSchema, cutoff: LocalTimeSchema })
    .openapi('VenueCutoffPeriod');
export const VenueSchema = z
    .object({
    id: VenueIdSchema,
    organizationId: OrganizationIdSchema,
    name: z.string().min(1).max(120),
    slug: VenueSlugSchema,
    timezone: TimezoneSchema,
    /**
     * When the venue's business day ends and the next begins, local time (spec/01; default 06:00) — the cutoff from
     * the next business day on, when a trading venue has just changed it (ADR-0029).
     */
    businessDayCutoff: LocalTimeSchema,
    /**
     * The cutoffs the venue traded on before, each with the last business day it decided, oldest first (ADR-0029). Empty
     * when it never changed. A change made while trading adds the day in progress here, so it and every past day keep
     * their cutoff; its last entry's next day is when `businessDayCutoff` takes effect.
     */
    businessDayCutoffHistory: z.array(VenueCutoffPeriodSchema),
    currency: CurrencyCodeSchema,
    address: AddressSchema.nullable(),
    coordinate: CoordinateSchema.nullable(),
    contact: VenueContactSchema,
    openingHours: OpeningHoursSchema,
    holidays: HolidaysSchema,
    amenities: VenueAmenitiesSchema,
    photos: VenuePhotosSchema,
    /**
     * The venue's own logo (F-ORG-01, spec/13 §13): a `venue_logo` upload, shown on its stations' lock screen
     * and in the kiosk's session rail, and on the console's header. Null: the organisation's logo, else the
     * venue's name as a wordmark.
     */
    logo: UploadedImageSchema.nullable(),
    status: VenueStatusSchema,
    /** Whether the player app's Venues list and venue page show it (rule 6). Only `live` venues appear. */
    publicListing: z.boolean(),
    /** Set by HQ for discovery ordering (spec/14 §3.3). */
    featured: z.boolean(),
    partner: z.boolean(),
    sponsored: z.boolean(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    archivedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('Venue', { description: 'A venue: where it is, when it is open, what it offers (spec/04 §3).' });
export const VenueListSchema = paginated(VenueSchema, 'VenueList');
export const CreateVenueSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    slug: VenueSlugSchema,
    timezone: TimezoneSchema,
    /** Must be the organisation's currency (rule 5). Omit it and the organisation's is used. */
    currency: CurrencyCodeSchema.optional(),
    businessDayCutoff: LocalTimeSchema.optional(),
    address: AddressSchema.optional(),
    coordinate: CoordinateSchema.optional(),
    contact: VenueContactSchema.optional(),
    openingHours: OpeningHoursSchema.optional(),
    holidays: HolidaysSchema.optional(),
    amenities: VenueAmenitiesSchema.optional(),
    publicListing: z.boolean().optional(),
})
    // No `organizationId`, `status`, `featured`, `partner` or `sponsored`: the organisation comes from
    // the scope, a new venue starts in `setup`, and the listing flags are HQ's.
    .strict()
    .openapi('CreateVenue');
export const UpdateVenueSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    slug: VenueSlugSchema,
    timezone: TimezoneSchema,
    businessDayCutoff: LocalTimeSchema,
    address: AddressSchema.nullable(),
    coordinate: CoordinateSchema.nullable(),
    contact: VenueContactSchema,
    openingHours: OpeningHoursSchema,
    holidays: HolidaysSchema,
    amenities: VenueAmenitiesSchema,
    photos: VenuePhotoRefsSchema,
    /** The venue's logo: a completed `venue_logo` upload of this venue, or null to remove it. */
    logoFileId: FileIdSchema.nullable(),
    status: EditableVenueStatusSchema,
    publicListing: z.boolean(),
})
    .partial()
    // `currency` is not editable, and the listing flags are HQ's (`PATCH …/listing`). `.strict()`
    // makes sending one a 400 rather than a silently ignored field.
    .strict()
    .refine((patch) => Object.keys(patch).length > 0, { message: 'Send at least one field to change.' })
    .openapi('UpdateVenue');
export const UpdateVenueListingSchema = z
    .object({
    featured: z.boolean(),
    partner: z.boolean(),
    sponsored: z.boolean(),
})
    .partial()
    .strict()
    .refine((patch) => Object.keys(patch).length > 0, { message: 'Send at least one flag.' })
    .openapi('UpdateVenueListing', { description: 'HQ discovery flags (spec/14 §3.3).' });
// ---------------------------------------------------------------------------------------------
// The settings this feature introduces (spec/13 §17). The keys are declared by `org`; the value
// shapes are here so the console's generated settings screen and the server agree on them.
// ---------------------------------------------------------------------------------------------
/** `venue.closingAction` (spec/05 §12): what closing time does to running sessions. */
export const VenueClosingActionSchema = z.enum(['warn_only', 'end_sessions_at_close']).openapi('VenueClosingAction');
/**
 * `venue.powerSchedule` (spec/10 §6.5): wake every station at opening time minus a lead, shut down
 * idle, locked ones at closing time plus a grace, never during a session. `off` by default, which is
 * what every venue does today.
 */
export const VenuePowerScheduleSchema = z
    .discriminatedUnion('mode', [
    z.object({ mode: z.literal('off') }).strict(),
    z
        .object({
        mode: z.literal('opening_hours'),
        wakeLeadMinutes: z.int().min(0).max(120),
        shutdownGraceMinutes: z.int().min(0).max(240),
    })
        .strict(),
])
    .openapi('VenuePowerSchedule');
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
/** `If-Match` — the `ETag` the caller read (spec/08 §2.5). Required on every edit of a venue. */
export const ConditionalWriteHeaderSchema = z
    .object({
    'if-match': z.string().min(1).max(64).openapi({ description: 'The ETag this write is based on.' }),
})
    .openapi('ConditionalWriteHeaders');
/** A single venue, with its version as a strong ETag. */
export function venueResponse(description) {
    return {
        description,
        headers: z.object({ ETag: z.string().openapi({ description: 'The version to send back as If-Match.' }) }),
        content: { [JSON_CONTENT_TYPE]: { schema: VenueSchema } },
    };
}
const notFound = problemResponse('No such venue in this organisation, or not one you can see.', 'not-found');
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
export const listVenuesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues',
    operationId: 'listVenues',
    tags: ['venues'],
    summary: "List the organisation's venues",
    description: 'Archived venues are left out, and a member who works at some venues only is shown only those ' +
        '(spec/07 §6.1). Requires `venues:read`.',
    [PERMISSION_EXTENSION]: VENUES_READ,
    request: { params: OrganizationScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: { description: 'A page of venues.', content: { [JSON_CONTENT_TYPE]: { schema: VenueListSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such organisation, or not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createVenueRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues',
    operationId: 'createVenue',
    tags: ['venues'],
    summary: 'Create a venue',
    description: "Starts in `setup`. The currency must be the organisation's (omit it to take the organisation's). " +
        'Starts with pay as you go off — `sessions.payAsYouGoEnabled` stored `false` at the venue, audited — unless the ' +
        'organisation has chosen a value for it, which then applies (ADR-0058). Requires `venues:create`.',
    [PERMISSION_EXTENSION]: VENUES_CREATE,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: CreateVenueSchema } }, required: true },
    },
    responses: {
        201: venueResponse('The venue that was created.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such organisation, or not one you can see.', 'not-found'),
        409: problemResponse('Another venue already has that slug.', 'slug-taken'),
        422: problemResponse("The currency is not the organisation's.", 'currency-mismatch'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getVenueRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}',
    operationId: 'getVenue',
    tags: ['venues'],
    summary: 'Read a venue',
    description: 'Returns the venue and its `ETag`. Requires `venues:read`.',
    [PERMISSION_EXTENSION]: VENUES_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: venueResponse('The venue.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateVenueRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/venues/{venueId}',
    operationId: 'updateVenue',
    tags: ['venues'],
    summary: 'Edit a venue',
    description: 'Send the `ETag` you read as `If-Match`. A stale one is 412 and nothing is written; read the venue ' +
        'again and re-apply. `timezone` changes only while the venue is in `setup`; once it trades, a ' +
        '`businessDayCutoff` change takes effect from the next business day, the day in progress and past days keeping ' +
        'theirs (ADR-0029). `status: live` from `setup` waits on the venue’s essential setup steps — opening hours, a ' +
        'price players can pay, a PC paired — and is `409 venue-not-ready` with `missingSteps` until they are done ' +
        '(ADR-0047); pausing and resuming a venue that has opened are not asked. ' +
        'Requires `venues:update`. Every change is audited with the before and after.',
    [PERMISSION_EXTENSION]: VENUES_UPDATE,
    request: {
        headers: ConditionalWriteHeaderSchema,
        params: VenueScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: UpdateVenueSchema } }, required: true },
    },
    responses: {
        200: venueResponse('The venue as it is now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        409: problemResponse('The slug is taken, the change is not allowed in this status, or the venue is not ready to go live (its essential setup steps, `missingSteps`).', 'slug-taken', 'conflict', 'venue-not-ready'),
        412: problemResponse('Someone else changed the venue since you read it.', 'stale-write'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const archiveVenueRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/archive',
    operationId: 'archiveVenue',
    tags: ['venues'],
    summary: 'Close a venue for good',
    description: 'Sets the status to `closed`, takes it out of the public listing and out of the venue list. ' +
        'Nothing is deleted: its orders and sessions are financial records (spec/04 §1.4). ' +
        'Requires `venues:archive` and `If-Match`.',
    [PERMISSION_EXTENSION]: VENUES_ARCHIVE,
    request: { headers: ConditionalWriteHeaderSchema, params: VenueScopeParamsSchema },
    responses: {
        200: venueResponse('The archived venue.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        409: problemResponse('The venue is already archived.', 'conflict'),
        412: problemResponse('Someone else changed the venue since you read it.', 'stale-write'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// HQ: every venue on the Platform (spec/14 §3.3, F-HQ-04)
// ---------------------------------------------------------------------------------------------
//
// One list of every venue, whoever owns it, with each venue's listing flags, so HQ sets them from the
// list (`PATCH …/listing` below, which takes the `organizationId` each row carries). **Reading it is
// reaching into every organisation it shows**, so the read is audited the way HQ's read of the audit
// log is (spec/07 §11.2): one platform-level row with the filter, and one row in the log of every
// organisation whose venues are on the page, before the page leaves the server. A venue is not an
// organisation's customer data, so `organization.hqAccess` does not narrow the list (spec/07 §7).
//
// The row carries what exists today: where the venue is, its hours, its status, its listing flags
// and its photos. The stations, the client versions, the online state and the photos awaiting
// moderation that spec/14 §3.3 also lists are added to it — additively — by the features that own
// them (F-STN-02, F-HQ-32, F-HQ-34, photo moderation), exactly as the player profile grows.
/** `status=setup,live`: any of the listed statuses. A plain string on the wire, checked here. */
const VENUE_STATUS_LIST = new RegExp(`^(?:${VENUE_STATUSES.join('|')})(?:,(?:${VENUE_STATUSES.join('|')}))*$`);
const VenueStatusListParamSchema = z
    .string()
    .regex(VENUE_STATUS_LIST, `Comma-separated, from: ${VENUE_STATUSES.join(', ')}`)
    .openapi({ description: 'Comma-separated; a venue in any of them.', example: 'setup,live,paused' });
/** The statuses a `status` query parameter names, parsed. */
export function parseVenueStatusList(value) {
    if (value === undefined)
        return [];
    return [...new Set(value.split(','))].map((part) => VenueStatusSchema.parse(part));
}
const FlagParamSchema = z.enum(['true', 'false']);
export const HqVenueQuerySchema = PageQuerySchema.extend({
    organizationId: OrganizationIdSchema.optional().openapi({ description: 'Only this organisation’s venues.' }),
    q: z
        .string()
        .trim()
        .min(2)
        .max(120)
        .optional()
        .openapi({ description: 'Part of the venue’s name or slug, in any case.' }),
    city: z.string().trim().min(1).max(80).optional().openapi({ description: 'The address locality, case-insensitive.' }),
    status: VenueStatusListParamSchema.optional(),
    publicListing: FlagParamSchema.optional(),
    featured: FlagParamSchema.optional(),
    partner: FlagParamSchema.optional(),
    sponsored: FlagParamSchema.optional(),
})
    // Unknown parameters are refused, never turned into a filter (spec/08 §2.4).
    .strict()
    .openapi('HqVenueQuery');
export const HqVenueOrganizationSchema = z
    .object({
    id: OrganizationIdSchema,
    name: z.string(),
})
    .openapi('HqVenueOrganization', { description: 'The organisation a venue belongs to, as HQ’s venue list names it.' });
export const HqVenueSchema = z
    .object({
    venue: VenueSchema,
    organization: HqVenueOrganizationSchema,
})
    .openapi('HqVenue', { description: 'A venue on HQ’s list of every venue, with the organisation it belongs to (spec/14 §3.3).' });
export const HqVenueListSchema = paginated(HqVenueSchema, 'HqVenueList');
export const listHqVenuesRoute = createRoute({
    method: 'get',
    path: '/v1/hq/venues',
    operationId: 'listHqVenues',
    tags: ['hq'],
    summary: 'Every venue on the Platform',
    description: 'Every organisation’s venues, archived ones included, oldest first, with their listing flags. Filters: ' +
        '`organizationId`, `q` (name or slug), `city`, `status`, and each flag. The read is audited: one ' +
        'platform-level row with the filter, and one row in the log of every organisation whose venues are on the ' +
        'page. Platform scope: requires `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { query: HqVenueQuerySchema },
    responses: {
        200: { description: 'A page of venues.', content: { [JSON_CONTENT_TYPE]: { schema: HqVenueListSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// HQ: the discovery flags (spec/14 §3.3, F-ORG-01 rule 6)
// ---------------------------------------------------------------------------------------------
export const updateVenueListingRoute = createRoute({
    method: 'patch',
    path: '/v1/hq/organizations/{organizationId}/venues/{venueId}/listing',
    operationId: 'updateVenueListing',
    tags: ['hq'],
    summary: 'Set a venue’s discovery flags',
    description: 'Featured, Partner and Sponsored order the player app’s Venues list. Only the flags sent change, ' +
        'and nobody but HQ sets them, so there is no other editor to race and no `If-Match`; the venue’s ' +
        'ETag still moves, because its representation did. Platform scope: requires `hq:organizations` and ' +
        'a second factor. Reaching into the organisation is audited in its own log.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: UpdateVenueListingSchema } }, required: true },
    },
    responses: {
        200: venueResponse('The venue as it is now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such venue in that organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=venues.js.map